/* eslint-disable jsdoc/require-jsdoc */
import { useState } from 'react';
import { Flex } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import WizardSteps from 'components/WizardSteps/WizardSteps';
import { STEPS_WIZARD_STEPS_PROPS, textBySensor } from './constants';
import CopyTextArea from 'components/CopyTextArea';
import TabList from 'components/TabList';
import {
  BoldText,
  CodeText,
  DefaultText,
  InitialStepWrapper,
  PipelineImage,
  StepsWrapper
} from './GitstreamWizardModal.style';
import { redirectToProvider } from 'common/utils/utils';
import AppRouter from 'common/AppRouter';
import { showOAuthExplanationModal } from 'modals/personalAccessToken.service';
import { SENSOR_NAMES } from '@linearb/linjs-common';
import enablePipelinesGSbb from 'images/bb-pipelines-enabled.png';
import { LBButton } from '@linearb/buzz';

export const NextPreviousButtons = ({
  onNextClick,
  onPreviousClick,
  nextTitle = 'Next'
}) => (
  <Flex gap='1rem'>
    <LBButton variant='outline' onClick={onPreviousClick}>
      <Icon name='ArrowLeft' /> Previous
    </LBButton>
    {nextTitle !== 'Skip' && (
      <LBButton onClick={onNextClick}>
        <Icon name='ArrowRight' color='white' /> {nextTitle}
      </LBButton>
    )}
  </Flex>
);

export const InitialStep = ({ sensorName }) => (
  <InitialStepWrapper direction='column'>
    <DefaultText mb='2.5rem'>THE INSTALLATION INCLUDES 3 STEPS:</DefaultText>
    <Flex gap='1.5rem'>
      <WizardSteps {...STEPS_WIZARD_STEPS_PROPS.INITIAL} />
      <Flex direction='column' mt='0.1rem'>
        <DefaultText mb='3.5rem'>
          Designate a gitStream user account
        </DefaultText>
        <Flex mb='3.5rem'>
          <DefaultText>Create a&nbsp;</DefaultText>
          <CodeText>cm</CodeText>
          <DefaultText>&nbsp;repo with&nbsp;</DefaultText>
          {textBySensor[sensorName]?.ymlFileName}
          <DefaultText>&nbsp;script</DefaultText>
        </Flex>
        <Flex>
          <DefaultText>Create&nbsp;</DefaultText>
          <CodeText>.cm</CodeText>
          <DefaultText>&nbsp;automation files</DefaultText>
        </Flex>
      </Flex>
    </Flex>
    <Flex mt='11rem'>
      <Icon name='infooutlined' color='waikawaGray' size='x-large' />
      {textBySensor[sensorName]?.prerequisites}
    </Flex>
  </InitialStepWrapper>
);

export const FirstStep = ({ sensorName }) => (
  <StepsWrapper direction='column'>
    <Flex gap='1rem'>
      <WizardSteps {...STEPS_WIZARD_STEPS_PROPS.FIRST} />
      <Flex direction='column' pt='tiny' ml='large'>
        <DefaultText mb='1rem'>CREATE A CM REPO WITH CI SCRIPT</DefaultText>
        <Flex gap='0.4rem'>
          <DefaultText>1.</DefaultText>
          <div>
            <DefaultText>
              In your {textBySensor[sensorName]?.title} workspace, Create a new
              repository named &nbsp;
            </DefaultText>
            <CodeText>cm</CodeText>
            <DefaultText>
              &nbsp; Ensure it is in the same project as the target repositories
              to maintain proper linkage and integration.
            </DefaultText>
          </div>
        </Flex>
        {sensorName === SENSOR_NAMES.BITBUCKET && (
          <div>
            <Flex gap='0.4rem'>
              <DefaultText>2.</DefaultText>
              <div>
                <DefaultText>In the newly created </DefaultText>
                <CodeText>cm</CodeText>
                <DefaultText>
                  &nbsp;repository, navigate to &nbsp;
                  <BoldText>Repository Settings</BoldText> {' > '}
                  <BoldText>Pipelines</BoldText>
                  {' > '}
                  <BoldText>Settings</BoldText>
                  &nbsp;and ensure that pipelines are enabled.
                </DefaultText>
              </div>
            </Flex>

            <PipelineImage src={enablePipelinesGSbb} alt='enable pipelines' />
          </div>
        )}
        <Flex mb='small' gap='0.4rem'>
          <DefaultText>
            {sensorName === SENSOR_NAMES.BITBUCKET ? 3 : 2}.
          </DefaultText>
          <div>
            <DefaultText>Create a&nbsp;</DefaultText>
            {textBySensor[sensorName]?.ymlFileName}
            <DefaultText>&nbsp;file in your new&nbsp;</DefaultText>
            <CodeText>cm</CodeText>
            <DefaultText>
              &nbsp;repository's default branch (usually &nbsp;
              <CodeText>master</CodeText> or <CodeText>main</CodeText>) and add
              the following configuration.
            </DefaultText>
          </div>
        </Flex>
        <CopyTextArea
          height='24.5rem'
          width='58rem'
          text={textBySensor[sensorName]?.ymlFileContent}
        />
      </Flex>
    </Flex>
  </StepsWrapper>
);
const SecondStepTabs = ({ sensorName }) => {
  const tabs = [
    {
      name: 'Single Repo Setup',
      type: 'default'
    },
    { name: `${textBySensor[sensorName]?.title} group Setup` }
  ];
  const TAB_PROPERTIES = {
    [tabs[0].name]: {
      text: (
        <div>
          <DefaultText color='shuttleGray'>
            In your repo’s default branch, create a&nbsp;
          </DefaultText>
          <CodeText color='shuttleGray'>.cm/gitstream.cm</CodeText>
          <DefaultText color='shuttleGray'>&nbsp;file.</DefaultText>
        </div>
      ),
      codeHeight: '19.4rem'
    },
    [tabs[1].name]: {
      text: (
        <div>
          <DefaultText color='shuttleGray'>
            For consistent rules across all repos in your
            {textBySensor[sensorName]?.title} group, create&nbsp;
          </DefaultText>
          <CodeText color='shuttleGray'>gitstream.cm</CodeText>
          <DefaultText color='shuttleGray'>
            &nbsp;rules file in the root directory of your cm repository’s
            default branch. Unlike single repo setup, place the&nbsp;
          </DefaultText>
          <CodeText color='shuttleGray'>gitstream.cm</CodeText>
          <DefaultText color='shuttleGray'>
            &nbsp;file in the cm repository’s root directory.
          </DefaultText>
        </div>
      ),
      codeHeight: '15rem'
    }
  };
  const [currentTab, setCurrentTab] = useState(tabs[0].name);
  const { text, codeHeight } = TAB_PROPERTIES[currentTab];
  return (
    <Flex direction='column' gap='1.5rem'>
      <TabList
        tabs={tabs}
        selectedTab={currentTab}
        onTabClick={tab => setCurrentTab(tab.name)}
      />
      {text}
      <CopyTextArea
        height={codeHeight}
        width='58rem'
        text={textBySensor[sensorName]?.cmFile}
      />
    </Flex>
  );
};
export const SecondStep = ({ sensorName }) => (
  <StepsWrapper gap='1rem'>
    <WizardSteps {...STEPS_WIZARD_STEPS_PROPS.SECOND} />
    <Flex direction='column' ml='large' mt='tiny'>
      <DefaultText mb='1rem'>CREATE YOUR FIRST AUTOMATION</DefaultText>
      <DefaultText>
        Set up gitStream automation rules for a single repo or your entire
        {textBySensor[sensorName]?.title} organization. Choose the tab below for
        your preferred instructions.
      </DefaultText>
      <SecondStepTabs sensorName={sensorName} />
    </Flex>
  </StepsWrapper>
);

export const ThirdStep = ({ sensorName }) => (
  <StepsWrapper gap='1rem'>
    <WizardSteps {...STEPS_WIZARD_STEPS_PROPS.THIRD} />
    <Flex direction='column' ml='large' pt='tiny'>
      <DefaultText mb='1rem'>DESIGNATE A GITSTREAM ACCOUNT</DefaultText>
      <div style={{ width: '50rem' }}>
        {textBySensor[sensorName]?.designateAccount}
      </div>
    </Flex>
  </StepsWrapper>
);

export const ForthStep = ({ sensorName: provider, sensorAuth }) => {
  const queryParams = {
    sensor_auth_id: sensorAuth.id,
    cacheCreds: true
  };
  return showOAuthExplanationModal(provider, false, provider =>
    redirectToProvider(`${provider}_gitstream`, {
      redirectPath: `${AppRouter.REPOSITORIES.path}?isOrgSettings=true&gitstreamFlow=true`,
      query: queryParams
    })
  );
};
